import { useCallback, useContext, useRef, useState } from "react"

import { useRouter } from "expo-router"

import { Index, ProviderConnector, ProviderConnectorCollection } from "wealthmode-frontend/backend"
import { Box, Button, ButtonText, HStack, Text } from "wealthmode-frontend/gluestack/ui"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

/** Only provider connectors nothing has claimed can be curated. */
const UNLINKED = "eq(unlinked,true)"

/**
 * Orphans are provider connectors no connector claims. Auto-curation only ever
 * runs the first time a provider reports an institution, so anything ambiguous
 * then - or unlinked by hand since - waits here to be curated by a human.
 */
export default function ProviderConnectors() {
  const backend = useContext(BackendContext)
  const router = useRouter()

  const collectionRef = useRef<ProviderConnectorCollection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)

  const load = useCallback(async () => {
    const data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    const collection = data.link("provider_connectors")?.resolve() as ProviderConnectorCollection | undefined
    if (!collection) {
      return undefined
    }

    await collection.fetch({ query: new URLSearchParams({ where: UNLINKED }), reload: true })

    collectionRef.current = collection

    return collection
  }, [backend])

  async function promote(model: ProviderConnector) {
    setError(null)
    setCreating(model.id)

    try {
      const listLink = collectionRef.current?.link("self")
      const connectorsLink = (backend.link("data").resolve() as Index).link("connectors")
      if (!listLink || !connectorsLink) {
        setError("Connectors resource is not available")
        return
      }

      const response = await connectorsLink.send({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider_connector: model.id }),
      })

      if (!response.ok) {
        const text = await response.text()
        setError(text || response.statusText)
        return
      }

      const created = (await response.json()) as { id: string }
      router.push(`/connectors/${created.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreating(null)
    }
  }

  return (
    <Box className="flex flex-1 min-h-0 flex-col">
      <DataTable
        title={"Unlinked provider connectors"}
        load={load}
        search={{ mode: "remote" }}
        header={[
          { id: "id", title: "ID", shrink: true },
          { id: "provider", title: "Provider", shrink: true },
          { id: "name", title: "Name" },
          { id: "domain", title: "Domain" },
          { id: "status", title: "Status", shrink: true },
          { id: "actions", title: "", shrink: true, noTruncate: true },
        ]}
        render={(model: ProviderConnector, field: string) => {
          switch (field) {
            case "domain":
              try {
                return new URL(model.attributes.url).hostname
              } catch (e) {
                return model.attributes.url
              }

            case "status":
              return model.attributes.active ? "Active" : "Inactive"

            case "actions":
              return (
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={creating === model.id}
                  onPress={() => void promote(model)}
                >
                  <ButtonText>Create connector</ButtonText>
                </Button>
              )
          }

          return model.get(field)
        }}
      />
      {error ? (
        <HStack className="px-4 py-2">
          <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
        </HStack>
      ) : null}
    </Box>
  )
}
